import { type JSX, type PropsWithChildren, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { type Claim, COUNTER_NOTHING_READ } from '../coverage';
import { formatCount, formatEuros } from '../format';
import { METHODOLOGY_COUNTER_ANCHOR, METHODOLOGY_COUNTER_LINK, METHODOLOGY_PATH } from '../labels';
import { type CounterFlavour, quantiseCounter } from '../useLiveCounter';
import { InfoPopover } from './InfoPopover';
import { Skeleton } from './ui/skeleton';

/**
 * The ticking figure and the group it may only appear inside (ui-direction §5, ux-spec §3).
 * Two contracts are carried by the types rather than by review: a counter cannot be rendered
 * without its own note, and a group of counters cannot be rendered without the honesty caption —
 * `note` and `claim` are required props, which is `StatBadge`'s trick applied one level up.
 */

const MOUNT_MS = 600;

const TILE =
  'flex flex-col gap-0.5 rounded-md border border-line bg-surface p-[0.9rem_1.05rem_0.85rem] text-ink no-underline shadow-card';

/** Ease-out cubic: the count-up decelerates into its value instead of stopping dead. */
const ease = (t: number): number => 1 - (1 - t) ** 3;

/** The figure part-way through the mount count-up, quantised the same way the settled figure is:
 * an un-quantised intermediate prints cents that the value it lands on does not have. */
export function counterFrame(value: number, elapsed: number, flavour: CounterFlavour): number {
  return quantiseCounter(value * ease(Math.max(0, elapsed)), flavour);
}

/** The blue-light dot, and the only place on the site it appears (ui-direction §8.1). A frozen
 * counter's goes hollow and static — stale data is given no motion at all. */
function Beacon({ frozen }: { frozen: boolean }): JSX.Element {
  return (
    <span aria-hidden="true" className="relative size-2 shrink-0">
      <span
        className={
          frozen
            ? 'absolute -inset-px rounded-full border-2 border-accent bg-surface'
            : 'absolute inset-0 rounded-full bg-beacon'
        }
      />
      {!frozen && (
        <span className="absolute -inset-[5px] animate-[beacon_2.5s_ease-out_infinite] rounded-full border-2 border-accent opacity-0 motion-reduce:animate-none" />
      )}
    </span>
  );
}

interface BaseCounterProps {
  label: string;
  /** Where the tile leads, or nothing: a counter standing on the page it would link to is not a
   * link, and a self-link is a promise of somewhere to go that is not kept. */
  href?: string;
  /** The figure, or `null` when there is none to show — a counter resting on 0 is a claim. */
  value: number | null;
  /** Whether `null` means "still loading" (skeleton) or "nothing counted yet" (an em dash). */
  pending: boolean;
  /** Required: what the figure counts — the euro denominator, or the tile's own context. */
  note: string;
  /** A frozen counter's `frozenCounterNote`, printed *beside* the note rather than instead of it:
   * why the number stopped and what it counts are two different claims, and both have to show. */
  status?: string | null;
  frozen: boolean;
  /** Whether the mount count-up may play. Never true for a frozen counter. */
  animate: boolean;
  lead?: boolean;
}

/**
 * A euro counter is a damage total, so it inherits `StatBadge`'s damage rule one component over:
 * `excluded` is required, and `null` — the honest value when nothing was doubted — has to be
 * passed rather than defaulted, so a tile that never considered the exclusion does not compile.
 */
export type LiveCounterProps = BaseCounterProps &
  ({ flavour: 'count' } | { flavour: 'euro'; excluded: string | null });

export function LiveCounter(props: LiveCounterProps): JSX.Element {
  const { label, href, value, pending, note, status = null, frozen, animate, lead = false } = props;
  const format = props.flavour === 'euro' ? formatEuros : formatCount;
  const excluded = props.flavour === 'euro' ? props.excluded : null;
  // Server-rendered and reduced-motion output starts finished, so the honest value is in the
  // markup from the first byte and no reader ever sees a zero that was never a measurement.
  const [elapsed, setElapsed] = useState(() => (typeof window === 'undefined' || !animate ? 1 : 0));

  useEffect(() => {
    if (!animate) {
      setElapsed(1);
      return;
    }
    const started = performance.now();
    let frame = requestAnimationFrame(function step(at: number) {
      const t = Math.min(1, (at - started) / MOUNT_MS);
      setElapsed(t);
      if (t < 1) frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [animate]);

  const body = (
    <>
      <span className="flex items-center gap-1.5 text-small font-semibold text-ink-secondary">
        <Beacon frozen={frozen} />
        {label}
      </span>
      {/* Nothing numeric is drawn before there is an answer — not the figure, and not the note
          under it, which would otherwise print a denominator of zero as though it were counted. */}
      {value === null && pending ? (
        <>
          <Skeleton className={lead ? 'my-1 h-12 w-48' : 'my-1 h-9 w-32'} />
          <Skeleton className="h-3 w-40" />
        </>
      ) : value === null ? (
        <>
          <span
            className={`font-display font-bold text-ink-muted ${lead ? 'text-hero leading-[1.08]' : 'text-counter leading-[1.12]'}`}
          >
            —
          </span>
          <span className="text-caption text-ink-muted">{COUNTER_NOTHING_READ}</span>
        </>
      ) : (
        <>
          <span
            className={`font-display font-bold tracking-[-0.015em] tabular-nums ${lead ? 'text-hero leading-[1.08]' : 'text-counter leading-[1.12]'}`}
          >
            {format(counterFrame(value, elapsed, props.flavour))}
          </span>
          <span className="text-caption text-ink-muted">{note}</span>
          {status !== null && status !== '' && (
            <span className="text-caption text-caution-ink">{status}</span>
          )}
        </>
      )}
      {/* Set apart by a rule rather than by colour, exactly as `StatBadge` sets it apart: it is a
          second, rarer fact about the figure, and it is never hidden behind an interaction. */}
      {value !== null && excluded !== null && excluded !== '' && (
        <span className="mt-1 border-t border-line pt-1 text-caption text-ink-muted">
          {excluded}
        </span>
      )}
    </>
  );

  return href === undefined ? (
    <div className={TILE}>{body}</div>
  ) : (
    <Link to={href} className={`${TILE} transition-colors hover:border-accent`}>
      {body}
    </Link>
  );
}

/** The group a counter lives in and the caption it may not live without: `claim` is required, comes
 * from `coverage.ts`, and is printed short with the full sentence one click away. Never reworded. */
export function LiveCounterGroup({
  claim,
  className = 'grid gap-3 md:grid-cols-[1.35fr_1fr_1fr]',
  children,
}: PropsWithChildren<{ claim: Claim; className?: string }>): JSX.Element {
  return (
    <div>
      <div className={className}>{children}</div>
      <p className="mt-2.5 max-w-[44rem] text-caption leading-normal text-ink-muted">
        {claim.short}
        <InfoPopover label="Loenduri metoodika">
          {claim.full}{' '}
          <Link to={`${METHODOLOGY_PATH}#${METHODOLOGY_COUNTER_ANCHOR}`} className="text-accent">
            {METHODOLOGY_COUNTER_LINK}
          </Link>
        </InfoPopover>
      </p>
    </div>
  );
}
